import type React from 'react'
import type { TFunction } from 'react-i18next'
import type { Dispatch } from '@reduxjs/toolkit'
import type { Business, User } from '@pbt/pbt-ui-components'

import type { FailedRefundTransaction } from '~/api/graphql/generated/types'

import { triggerErrorDoggo } from '../../../add-payment-dialog-v2/utils/errorDoggoUtils'
import { TransactionFailureReasonName } from '../../../add-payment-dialog-v2/utils/paymentErrorMapping'
import {
  extractRefundFailureReasonName,
  getGraphQLRefundErrorMessage,
  isTransactionNotSettledError,
} from './refundErrorMapping'

interface InitiateLinkedRefundTransactionDependencies {
  currentBusiness?: Business | null
  currentUser?: User | null
  dispatch: React.Dispatch<any>
  initiateRefundTransactionMutation: (options: {
    variables: {
      input: {
        amount: number
        externalDeviceId: string | null
        originalPaymentId: string
        refundInvoiceId: string
      }
    }
  }) => Promise<any>
  reduxDispatch: Dispatch<any>
  refundAmount: number
  refundInvoiceId: string
  selectedPaymentId: string
  setIsLoading: (isLoading: boolean) => void
  t: TFunction<'Dialogs'>
}

/**
 * Handles the linked refund transaction mutation with error handling.
 * Unlike unlinked refunds, this does not pass an externalDeviceId and does not
 * update the RefundFlowState or set isLoading to false on success.
 * On success, it sets the transaction ID and enables subscription listening.
 */
export const initiateLinkedRefundTransaction = async (
  deps: InitiateLinkedRefundTransactionDependencies,
): Promise<void> => {
  const {
    dispatch,
    reduxDispatch,
    initiateRefundTransactionMutation,
    refundAmount,
    selectedPaymentId,
    refundInvoiceId,
    setIsLoading,
    t,
    currentUser,
    currentBusiness,
  } = deps

  try {
    const result = await initiateRefundTransactionMutation({
      variables: {
        input: {
          amount: refundAmount,
          originalPaymentId: selectedPaymentId,
          refundInvoiceId,
          // Linked refunds do not require an external device ID
          externalDeviceId: null,
        },
      },
    })

    if (
      !result?.data?.initiateRefundTransaction ||
      result.data?.initiateRefundTransaction?.error?.failureReason ||
      result.errors
    ) {
      dispatch({ type: 'CANCEL_TRANSACTION' })
      // Check for specific GraphQL error types to show appropriate message
      const graphQLErrorMessage = getGraphQLRefundErrorMessage(
        result.errors,
        true, // linked = true
      )
      const errorMessage =
        graphQLErrorMessage ||
        t('Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE_LINKED')
      const backendErrorMessage =
        result.data?.initiateRefundTransaction?.error?.message ||
        result.errors?.[0]?.message ||
        errorMessage
      triggerErrorDoggo(
        reduxDispatch,
        backendErrorMessage,
        500,
        currentUser,
        currentBusiness,
      )
      dispatch({
        type: 'SET_REFUND_ERROR',
        payload: {
          message: errorMessage,
          severity: 'error',
          status: 500,
        },
      })
      // If failure reason is UNKNOWN or TRANSACTION_NOT_SETTLED, do not fall back to unlinked flow
      const failureReason = result.data?.initiateRefundTransaction?.error
        ?.failureReason
        ? extractRefundFailureReasonName(
            result.data?.initiateRefundTransaction as FailedRefundTransaction,
          )
        : null
      const shouldNotFallbackToUnlinked =
        failureReason === TransactionFailureReasonName.UNKNOWN ||
        failureReason ===
          TransactionFailureReasonName.TRANSACTION_NOT_SETTLED ||
        isTransactionNotSettledError(result.errors)

      if (failureReason && !shouldNotFallbackToUnlinked) {
        dispatch({ type: 'SET_LINKED', payload: false })
        dispatch({ type: 'SET_UNLINKED', payload: true })
        dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
      }
      setIsLoading(false)
    } else {
      dispatch({
        type: 'SET_TRANSACTION_ID',
        payload: result.data.initiateRefundTransaction.id,
      })
      if (result.data.initiateRefundTransaction.externalDeviceId) {
        dispatch({
          type: 'SET_EXTERNAL_DEVICE_ID',
          payload: result.data.initiateRefundTransaction.externalDeviceId,
        })
      }
      dispatch({
        type: 'SET_LINKED_REFUND_LISTENING',
        payload: true,
      })
    }
  } catch (err) {
    dispatch({ type: 'CANCEL_TRANSACTION' })
    const errorMessage = t(
      'Dialogs:REFUND_DIALOG_V2.ERRORS.REFUND_INITIATION_FAILURE_LINKED',
    )
    const backendErrorMessage = (err as any)?.message || errorMessage
    triggerErrorDoggo(
      reduxDispatch,
      backendErrorMessage,
      500,
      currentUser,
      currentBusiness,
    )
    dispatch({
      type: 'SET_REFUND_ERROR',
      payload: {
        message: errorMessage,
        severity: 'error',
        status: 500,
      },
    })
    dispatch({ type: 'SET_LINKED', payload: false })
    dispatch({ type: 'SET_UNLINKED', payload: true })
    dispatch({ type: 'SET_LINKED_REFUND_LISTENING', payload: false })
    setIsLoading(false)
  }
}
